import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { useConfirmation } from '../../../../../../hooks/useConfirmation';
import { useDeletePriceListItemForProductMutation } from 'features/products/redux/productApiSlice';

export const useDeleteOperations = (selectedProductRowId: number, refetchPriceList: () => void) => {
  const [deletePriceListItemForProduct] = useDeletePriceListItemForProductMutation();
  const { confirmation, showConfirmation, hideConfirmation, handleConfirm } = useConfirmation();
  
  const performDelete = useCallback(async (selectedRowOrAllItems: any, clearSelection: () => void) => {
    // Check if it's bulk delete (array) or single delete (object)
    const isAllItems = Array.isArray(selectedRowOrAllItems);
    
    if (isAllItems) {
      // Bulk delete all items
      const allItems = selectedRowOrAllItems;
      if (allItems.length === 0) {
        toast.error('No price list items to delete');
        return false;
      }
      
      try {
        // Delete all items in parallel
        const deletePromises = allItems.map(item => 
          deletePriceListItemForProduct({
            productRowId: selectedProductRowId,
            productPriceListRowId: item.rowId
          }).unwrap()
        );
        
        await Promise.all(deletePromises);
        toast.success(`Successfully deleted ${allItems.length} price list items`);
        refetchPriceList();
        clearSelection();
        return true;
      } catch (error) {
        toast.error('Failed to delete some or all price list items');
        return false;
      }
    } else {
      // Single delete
      const selectedRow = selectedRowOrAllItems;
      if (selectedRow) {
        try {
          await deletePriceListItemForProduct({
            productRowId: selectedProductRowId,
            productPriceListRowId: selectedRow.rowId
          }).unwrap();
          toast.success('Price list item deleted successfully');
          refetchPriceList();
          clearSelection();
          return true; // Success
        } catch (error) {
          toast.error('Failed to delete price list item');
          return false; // Failure
        }
      }
      return false;
    }
  }, [selectedProductRowId, deletePriceListItemForProduct, refetchPriceList]);
  
  const handleDelete = useCallback((selectedRowOrAllItems: any, clearSelection: () => void) => {
    const isAllItems = Array.isArray(selectedRowOrAllItems);
    
    if (isAllItems) {
      // Bulk delete confirmation
      const allItems = selectedRowOrAllItems;
      if (allItems.length === 0) {
        toast.error('No price list items to delete');
        return;
      }
      
      showConfirmation({
        title: 'Delete All Price List Items',
        message: `This will permanently remove ALL ${allItems.length} price list items from this product. This action cannot be undone.`,
        recordName: 'all price list items',
        recordId: `${allItems.length} items`,
        deleteButtonText: 'Yes, Delete All Items',
        onConfirm: async () => {
          await performDelete(allItems, clearSelection);
        }
      });
    } else {
      // Single delete confirmation
      const selectedRow = selectedRowOrAllItems;
      if (!selectedRow) {
        toast.error('Please select a price list item to delete');
        return;
      }
      
      showConfirmation({
        title: 'Delete Price List Item',
        message: 'This will permanently remove the price list item from this product. This action cannot be undone.',
        recordName: 'price list item',
        recordId: selectedRow.priceListName || selectedRow.rowId?.toString(),
        deleteButtonText: 'Yes, Delete Price List Item',
        onConfirm: async () => {
          await performDelete(selectedRow, clearSelection);
        }
      });
    }
  }, [showConfirmation, performDelete]);
  
  return {
    confirmation,
    handleDelete,
    hideConfirmation,
    handleConfirm
  };
};
